<template>
    <div
        class="lesson-manual-nav"
        v-if="!editable">
        <div class="mobile-nav">
            <b-dropdown
                class="lesson-manual-dropdown"
                right
                variant="primary"
                :text="selectedLessonIn.title">
                <b-dropdown-item
                    v-for="lesson in lessonsIn"
                    @click="updateSelectedLesson(lesson)"
                    :key="lesson.id">
                    {{ lesson.title }}
                </b-dropdown-item>
            </b-dropdown>
        </div>
        <ul class="desktop-nav">
            <li
                class="desktop-nav-item"
                v-for="lesson in lessonsIn"
                :class="[selectedLessonIn.id === lesson.id && 'active']"
                @click="$emit('change-lesson', lesson)"
                :key="lesson.id">
                {{ lesson.title }}
            </li>
        </ul>
    </div>
    <div
        class="lesson-manual-edit-nav"
        v-else>
        <draggable
            group="lesson-manual-edit-nav"
            ghost-class="drag-ghost"
            :list="lessons"
            handle=".lesson-handle"
            @change="lessonOrderChanged">
            <b-list-group-item
                class="lesson-manual-edit-item"
                v-for="(lesson, lessonKey) in lessons"
                :key="`${lesson.title}-${lessonKey}`"
                @click="updateSelectedLesson(lesson)"
                :active="lesson.id === selectedLessonIn.id">
                <div class="lesson-manual-name">
                    <span class="drag-button">
                        <i class="fas fa-fw fa-arrows-alt-v lesson-handle"></i>
                    </span>

                    <span class="title">
                        {{ lesson.title }}
                    </span>
                </div>

                <b-dropdown
                    class="ellipsis-nav lesson-manual-context-menu"
                    menu-class="show-border"
                    variant="primary"
                    no-caret
                    right>
                    <template #button-content>
                        <i class="fas fa-fw fa-ellipsis-v"></i>
                    </template>
                    <b-dropdown-item
                        class="btn-edit-lesson-manual"
                        @click.prevent.stop="openUpdateLessonModal(lesson)">
                        <span class="icon btn-edit"></span>
                        {{ $t('lesson.edit_lesson') }}
                    </b-dropdown-item>
                    <b-dropdown-item
                        class="btn-remove-lesson-manual"
                        @click.prevent.stop="removeLesson(lesson)">
                        <span class="icon btn-remove"></span>
                        {{ $t('lesson.delete_lesson') }}
                    </b-dropdown-item>
                </b-dropdown>
            </b-list-group-item>
        </draggable>
        <div class="add-lesson-manual">
            <b-button
                class="btn-add btn-new-lesson"
                variant="primary"
                @click="showCreateLessonModal">
                {{ $t('lesson.new_lesson') }}
            </b-button>
        </div>
        <design-2021-update-lesson-modal-component
            :parent-in="parentIn"
            :parent-type="parentType"
            @lesson-updated="updateLesson"
            ref="update-lesson-modal">
        </design-2021-update-lesson-modal-component>
    </div>
</template>

<script>
import draggable from 'vuedraggable';

export default {
    name: 'design-2021-group-lesson-manual-nav-component',
    components: {
        draggable,
    },
    props: {
        lessonsIn: {
            type: Array,
            required: true,
        },
        parentIn: {
            type: Object,
            required: true,
        },
        editable: {
            type: Boolean,
            required: false,
            default: false,
        },
        parentType: {
            type: String,
            required: true,
        },
        selectedLessonIn: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            lessons: this.lessonsIn,
        };
    },

    methods: {
        addLesson(lesson) {
            this.$emit('lesson-added', lesson);
            this.lessons.push(lesson);
            this.alert.success();
        },

        updateSelectedLesson(lesson) {
            this.$emit('change-lesson', lesson);
        },

        updateLesson(lessonNew) {
            this.$emit('lesson-updated', lessonNew);

            this.lessons = this.lessons.map((lessonOld) => {
                if (lessonOld.id === lessonNew.id) {
                    return lessonNew;
                }
                return lessonOld;
            });

            this.alert.success();
        },

        lessonOrderChanged() {
            let array = [];
            let count = 0;

            for (const lesson of this.lessons) {
                count++;
                array.push({
                    id: lesson.id,
                    order: count,
                });
            }

            let data = {
                parent_id: this.parentIn.id,
                parent_type: this.parentType,
                lessons: array,
            };

            this.axios.post(this.routes.updateLessonOrder(), data).then(
                (response) => {}, //no action required
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        showCreateLessonModal() {
            this.$emit('show-create-lesson-modal');
        },

        openUpdateLessonModal(lesson) {
            this.$refs['update-lesson-modal'].show(lesson);
        },

        removeLesson(lesson) {
            this.alert
                .confirm(
                    this.$t('common.remove') + ' ' + lesson.title,
                    this.$t('common.are_you_sure_you_want_to_delete_this_item'),
                )
                .then((confirm) => {
                    if (confirm) {
                        this.alert.saving();

                        this.axios.delete(this.routes.lessonManualLessonDelete(lesson.id)).then(
                            (response) => {
                                this.lessons = this.lessons.filter((lesson1) => lesson1.id !== lesson.id);
                                this.$emit('lesson-removed', lesson.id);
                                this.alert.success();
                            },
                            (error) => {
                                return this.alert.ajaxError(error);
                            },
                        );
                    }
                });
        },
    },

    watch: {
        lessonsIn: function (newVal) {
            this.lessons = newVal;
        },
    },
};
</script>

<style scoped lang="scss">
.lesson-manual-nav {
    margin: map-get($grid-gutter-spacing, 'forty-two') 0 $grid-gutter-width 0;
}

.lesson-manual-dropdown ::v-deep {
    width: 100%;

    .dropdown-toggle {
        display: flex;
        align-items: center;
        justify-content: space-between;
        text-align: left;
        width: 100%;
    }
}

.lesson-manual-edit-nav {
    background-color: $white;
    border-radius: $border-radius-content;
    padding: map-get($grid-gutter-spacing, 'fifteen');
    margin-bottom: map-get($grid-gutter-spacing, 'sixty');
}

.lesson-manual-edit-nav {
    .lesson-manual-edit-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        background-color: transparent;
        border: none;
        padding: map-get($grid-gutter-spacing, 'fifteen') 0;
        column-gap: map-get($grid-gutter-spacing, 'eight');

        .title {
            &:hover {
                text-shadow: 0 0 var(--primary);
            }
        }

        &:not(:last-child) {
            border-bottom: 2px dotted $border-color;
        }

        &.active {
            .drag-button {
                color: $black;
            }

            .title {
                color: var(--primary);
                text-shadow: 0 0 var(--primary);
            }
        }
    }

    .lesson-manual-name {
        display: flex;
        align-self: flex-start;
        column-gap: map-get($grid-gutter-spacing, 'eight');
        flex: 1;

        &:hover {
            cursor: pointer;
        }
    }

    .drag-button {
        &:hover {
            cursor: pointer;
        }
    }

    .lesson-manual-context-menu ::v-deep {
        .dropdown-toggle {
            color: $white !important;
            font-size: $default-font-size;
            line-height: 1;
            background-color: var(--primary);
        }

        .dropdown-menu {
            .icon {
                &:before,
                &:after {
                    margin-right: map-get($grid-gutter-spacing, 'eight');
                }
            }
        }
    }

    .add-lesson-manual {
        margin-top: 1.5rem;
    }
}

.desktop-nav {
    display: none;
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .lesson-manual-dropdown ::v-deep {
        .dropdown-toggle {
            flex: 0 0 auto;
            margin-left: auto;
            width: 275px;
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'xl')) {
    .lesson-manual-nav {
        margin: 0;
    }

    .mobile-nav {
        display: none;
    }

    .desktop-nav {
        display: block;
        background-color: $white;
        padding: map-get($grid-gutter-spacing, 'fifteen');
        list-style: none;
        margin-bottom: 0;

        .desktop-nav-item {
            letter-spacing: 0.1px;

            &:first-child {
                padding-bottom: map-get($grid-gutter-spacing, 'eight');
            }

            &:not(:last-child):not(:first-child) {
                padding-top: map-get($grid-gutter-spacing, 'eight');
                padding-bottom: map-get($grid-gutter-spacing, 'eight');
            }

            &:not(:last-child) {
                border-bottom: 2px dotted $text-color;
            }

            &:last-child {
                padding-top: map-get($grid-gutter-spacing, 'eight');
            }

            &:hover {
                cursor: pointer;
            }

            &:hover,
            &.active {
                text-shadow: 1px 0 0 $text-color;
            }
        }
    }
}
</style>
